import {
  defaultLocale,
  defaultToolId,
  isToolId,
  localeTags,
  supportedLocales,
  type AppLocale,
  type ToolId,
} from '../app/config';
import { createStorageKey } from './storage';

const localeStorageKey = 'dracula_locale';
const lastToolStorageKey = createStorageKey('app.lastTool');

type DocumentMetadata = {
  locale: AppLocale;
  title: string;
  description: string;
  keywords: string;
};

export function detectPreferredLocale(): AppLocale {
  if (typeof window === 'undefined') {
    return defaultLocale;
  }

  const storedLocale = window.localStorage.getItem(localeStorageKey);
  if (storedLocale && supportedLocales.includes(storedLocale as AppLocale)) {
    return storedLocale as AppLocale;
  }

  const browserLocales = window.navigator.languages ?? [
    window.navigator.language,
  ];
  for (const locale of browserLocales) {
    const normalized = locale.toLowerCase();
    if (normalized.startsWith('ru')) {
      return 'ru';
    }
    if (normalized.startsWith('en')) {
      return 'en';
    }
  }

  return defaultLocale;
}

export function rememberLocale(locale: AppLocale) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(localeStorageKey, locale);
  }
}

export function detectPreferredToolId(): ToolId {
  if (typeof window === 'undefined') {
    return defaultToolId;
  }

  const storedToolId = window.localStorage.getItem(lastToolStorageKey);
  return isToolId(storedToolId) ? storedToolId : defaultToolId;
}

export function rememberToolId(toolId: ToolId) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(lastToolStorageKey, toolId);
  }
}

function setMetaTag(
  attribute: 'name' | 'property',
  key: string,
  value: string
) {
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`
  );
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
}

export function setDocumentMetadata({
  locale,
  title,
  description,
  keywords,
}: DocumentMetadata) {
  document.documentElement.lang = localeTags[locale];
  document.title = title;

  setMetaTag('name', 'description', description);
  setMetaTag('name', 'keywords', keywords);
  setMetaTag('property', 'og:title', title);
  setMetaTag('property', 'og:description', description);
  setMetaTag('property', 'twitter:title', title);
  setMetaTag('property', 'twitter:description', description);

  if (typeof window !== 'undefined') {
    const currentUrl = `${window.location.origin}${window.location.pathname}`;
    setMetaTag('property', 'og:url', currentUrl);
    setMetaTag('property', 'twitter:url', currentUrl);
  }
}
